import { Box, Button, Modal, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useQuery } from 'react-query';
import CustomDropDown from '../../../components/CustomDropDown';
import FillLabelTxtField from '../../../components/FillLabelTxtField';
import ScrollBox from '../../../components/ScrollBox';
import { getReqFilterOptions } from '../../../services/req_service';
import { datePickerformat } from '../../../utils/format';

const EMPTY_MULTI = { cnorNames: [], cneeNames: [], picLocations: [], delLocations: [], shipmtStatuses: [] };

function FilterMdl({ modalOpen, handleModalClose, filterObj, handleFilterObj }) {
    const [formErrors, setFormErrors] = useState({});
    const [multi, setMulti] = useState(EMPTY_MULTI);
    const { t } = useTranslation();

    const { data: filterOptions } = useQuery(['reqFilterOptions'], getReqFilterOptions, { staleTime: 5 * 60 * 1000 });

    // Re-sync the multi-select state with the applied filters each time the modal opens.
    useEffect(() => {
        if (modalOpen)
            setMulti({ ...EMPTY_MULTI, ...Object.fromEntries(Object.keys(EMPTY_MULTI).map(k => [k, filterObj[k] || []])) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalOpen]);

    let txtFieldProps = { fontSize: 14, height: 38, width: 250 };

    const toDD = (arr) => (arr || []).map(v => ({ value: v, label: v }));

    const shipmtStatusArr = [
        { value: "pending", label: t("bo.filterMdl.pending") },
        { value: "cancel", label: t("bo.filterMdl.cancel") },
        { value: "confirm", label: t("bo.filterMdl.confirm") },
        { value: "transportation", label: t("bo.filterMdl.transportation") },
        { value: "awaitingUnloading", label: t("bo.filterMdl.awaitingUnloading") },
        { value: "unloaded", label: t("bo.filterMdl.unloaded") },
        { value: "completed", label: t("bo.filterMdl.completed") }];

    function handleMultiChange(e) {
        const { name, value } = e.target;
        setMulti((prev) => ({ ...prev, [name]: typeof value === 'string' ? value.split(',') : value }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const formData = new FormData(e.target);
        const inputObject = Object.fromEntries(formData);
        let errorsObj = validate(inputObject);
        setFormErrors(errorsObj);

        if (Object.keys(errorsObj).length > 0)
            return;

        handleFilterObj({ ...inputObject, ...multi });
        handleModalClose();
    }

    const clearFormFields = () => {
        setMulti(EMPTY_MULTI);
        handleFilterObj({
            fromDate: '',
            toDate: '',
            ...EMPTY_MULTI,
        });
        handleModalClose();
        setFormErrors({});
    };

    return (
        <Modal
            open={modalOpen}
            onClose={handleModalClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <form onSubmit={handleSubmit} >
                <Box sx={modalStyle}>
                    <Stack direction={'row'} alignItems={"center"} mb={3}>
                        <Typography sx={{ fontWeight: "bold", fontSize: 17, color: "rgba(0, 0, 0, 0.6)", mr: 1 }}>{t("bo.filterMdl.filter")}</Typography>
                    </Stack>
                    <ScrollBox height={"80%"}>
                        <Stack gap={2} alignItems="center">
                            <Stack direction={"row"} gap={3} mb={1}>
                                <FillLabelTxtField defaultValue={datePickerformat(filterObj.fromDate)} type={"date"} name="fromDate" title={t("bo.filterMdl.fromDate")} errMsg={formErrors.fromDate} {...txtFieldProps} />
                                <FillLabelTxtField defaultValue={datePickerformat(filterObj.toDate)} type={"date"} name="toDate" title={t("bo.filterMdl.toDate")} errMsg={formErrors.toDate} {...txtFieldProps} />
                            </Stack>
                            <Stack direction={"row"} gap={3}>
                                <CustomDropDown multiple value={multi.cnorNames} handleDDChange={handleMultiChange} name={"cnorNames"} title={t("bo.filterMdl.cnor")} ddArr={toDD(filterOptions?.cnorNames)} {...txtFieldProps} />
                                <CustomDropDown multiple value={multi.cneeNames} handleDDChange={handleMultiChange} name={"cneeNames"} title={t("bo.filterMdl.cnee")} ddArr={toDD(filterOptions?.cneeNames)} {...txtFieldProps} />
                            </Stack>
                            <Stack direction={"row"} gap={3}>
                                <CustomDropDown multiple value={multi.picLocations} handleDDChange={handleMultiChange} name={"picLocations"} title={t("bo.filterMdl.pickLocation")} ddArr={toDD(filterOptions?.picLocations)} {...txtFieldProps} />
                                <CustomDropDown multiple value={multi.delLocations} handleDDChange={handleMultiChange} name={"delLocations"} title={t("bo.filterMdl.delLocation")} ddArr={toDD(filterOptions?.delLocations)} {...txtFieldProps} />
                            </Stack>
                            <Stack direction={"row"} gap={3}>
                                <CustomDropDown multiple value={multi.shipmtStatuses} handleDDChange={handleMultiChange} name={"shipmtStatuses"} title={t("bo.filterMdl.shipSts")} ddArr={shipmtStatusArr} {...txtFieldProps} />
                                <Box width={250} />
                            </Stack>
                        </Stack>
                    </ScrollBox>
                    <Stack direction={"row"} justifyContent={"space-between"} sx={{ mt: 2, mb: 2 }}>
                        <Button variant="outlined" onClick={clearFormFields} sx={{ height: 40, width: 150 }}>{t("bo.filterMdl.clear")}</Button>
                        <Stack justifyContent={"flex-end"} direction={"row"} width={500}>
                            <Button variant="text" sx={{ height: 40, width: 120 }} onClick={handleModalClose}>{t("bo.filterMdl.cancel")}</Button>
                            <Button variant="contained" type='submit' sx={{ height: 40, width: 150 }}>{t("bo.filterMdl.save")}</Button>
                        </Stack>
                    </Stack>
                </Box>
            </form>
        </Modal>
    )
}

export default FilterMdl;

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 590,
    bgcolor: 'background.paper',
    border: '2px solid #ffff',
    boxShadow: 24,
    px: 3,
    py: 2,
    borderRadius: 2,
    height: "auto",
    maxHeight: "90%"
};

function validate(values) {
    const errors = {};

    // Dates are optional when using the field filters, but must come as a pair.
    if (values.fromDate && !values.toDate)
        errors.toDate = "To Date is required!";

    if (!values.fromDate && values.toDate)
        errors.fromDate = "From Date is required!";

    return errors;
};
